import { useParams } from 'react-router';
import React, { MouseEventHandler, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import ConnectedFormContext from './Context/ConnectedFormContext';
import { Button, InfoButton, PrimaryButton, SuccessButton } from './connectedForm/Button';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../state/modules/service';
import { PrimaryTheme, SuccessTheme } from '../styles/themes/default/defaultFluentTheme';
import { grey } from '../styles/themes/default/defaultTheme';
import { merge } from 'lodash';
import { SubmitOption } from '../@types/connectedFormTypes';
import { AlertType } from '../graphql/schema/p4types';
import { ButtonPropsType } from './connectedForm/Button/Button';
import { IContextualMenuProps } from '@fluentui/react';
import { useP4ToastController } from '../views/system/NotificationsArea';
import { useFormContext } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types';

export type ISubmitOptionEntry = { value: SubmitOption; label: string; condition?: (params: any) => boolean };

export const SAVE_OPTIONS: Array<ISubmitOptionEntry> = [
  {
    value: SubmitOption.Exit,
    label: 'Save & exit',
  },
  {
    value: SubmitOption.Continue,
    label: 'Save & continue',
  },
  {
    value: SubmitOption.New,
    label: 'Save & add new',
    condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
  },
];

export const SAVE_DRAFT_OPTIONS: Array<ISubmitOptionEntry> = [
  {
    value: SubmitOption.Exit,
    label: 'Save draft & exit',
  },
  {
    value: SubmitOption.Continue,
    label: 'Save draft & continue',
  },
  {
    value: SubmitOption.New,
    label: 'Save draft & add new',
    condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
  },
];

export const SAVE_CONFIRM_OPTIONS: Array<ISubmitOptionEntry> = [
  {
    value: SubmitOption.Exit,
    label: 'Confirm & exit',
  },
  {
    value: SubmitOption.Continue,
    label: 'Confirm & continue',
  },
  {
    value: SubmitOption.New,
    label: 'Confirm & add new',
    condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
  },
];

export const POST_OPTIONS: Array<ISubmitOptionEntry> = [
  {
    value: SubmitOption.Exit,
    label: 'Post & exit',
  },
  {
    value: SubmitOption.New,
    label: 'Post & add new',
    condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
  },
];

export const SAVE_ATTACHMENT_OPTIONS: Array<ISubmitOptionEntry> = [
  {
    value: SubmitOption.Exit,
    label: 'Save attachment',
  },
  {
    value: SubmitOption.Continue,
    label: 'Save attachment & continue',
  },
];

/**
 *
 * @param unfilteredOptions
 * @param onClick
 * @param disabled  - Optional: If 'true' the button will be disabled (It will also be disabled if a submit is in progress).
 * @param temporaryDisableDelayOnClick - After click, the button will be disabled for this amount of time.
 * @returns Props for the Fluent-UI button that will be displayed.
 */
export const useSelectButtonProps = (
  unfilteredOptions: Array<ISubmitOptionEntry>,
  onClick: (event: MouseEvent | undefined, submitOption: SubmitOption) => void,
  disabled = false,
  temporaryDisableDelayOnClick: number
) => {
  const params = useParams();
  const { dispatchSimpleToast } = useP4ToastController();

  // Local submit options only used in react-hook-form based forms.
  // Otherwise we will be using the submitOptions passed through from ConnectedForm.
  const localSubmitOptions = useReducer(
    (state, action) => {
      switch (action.type) {
        case SubmitOption.Exit:
          return { ...state, value: SubmitOption.Exit };
        case SubmitOption.Continue:
          return { ...state, value: SubmitOption.Continue };
        case SubmitOption.New:
          return { ...state, value: SubmitOption.New };
        default:
          throw new Error();
      }
    },
    { value: SubmitOption.Exit }
  );
  const {
    submitOptions = localSubmitOptions,
    submitInProgress,
    pristine: connectedFormPristine,
  } = useContext(ConnectedFormContext);
  const formContext = useFormContext() as UseFormReturn | null;
  let pristine: boolean;
  if (formContext) {
    // If we find a formContext, this is a new react-hook-form based form, such as Stock Take.
    // We can determine the pristine status from the formState.
    pristine = !formContext.formState.isDirty;
  } else {
    // Otherwise, get it from connectedForm.
    pristine = connectedFormPristine;
  }

  const [selectedSubmitOption, dispatchSubmitOptions] = submitOptions;
  const options = unfilteredOptions.filter((option) => !option.condition || option.condition(params));
  const currentSaveOption = options.find((option) => option.value === selectedSubmitOption?.value) || options[0];

  // Used to defer action until 'submitOptions' is updated.
  const [actionTriggered, setActionTriggered] = useState(false);
  // Used to disable the button for n milliseconds after click (General double-click prevention).
  const [fixedDisabled, setFixedDisabled] = useState<boolean>(false);

  const handleAction = useCallback(
    (event: unknown | undefined, selectedOption: SubmitOption) => {
      const submitOption = selectedOption ?? selectedSubmitOption?.value ?? SubmitOption.Exit;

      if (submitInProgress) {
        dispatchSimpleToast('Submit already in progress', 'Please wait', AlertType.Warning);
        return;
      } else if (temporaryDisableDelayOnClick && temporaryDisableDelayOnClick > 0) {
        setFixedDisabled(true);
        setTimeout(() => setFixedDisabled(false), temporaryDisableDelayOnClick);
      }
      onClick(undefined as any, submitOption);
    },
    [dispatchSimpleToast, onClick, selectedSubmitOption?.value, submitInProgress, temporaryDisableDelayOnClick]
  );

  const menuProps: IContextualMenuProps | undefined =
    options.length > 1
      ? {
          items: options.map((option) => {
            return {
              key: option.value,
              text: option.label,
              onClick: () => {
                if (selectedSubmitOption?.value === option.value) {
                  // If we've selected the current option type, we can directly call the action handler instead of deferring.
                  handleAction(undefined, selectedSubmitOption.value);
                } else {
                  setActionTriggered(true);
                  dispatchSubmitOptions?.({ type: option.value }); //Update the submission type against the form.
                }
              },
              tooltip: option.tooltip,
            };
          }),
        }
      : undefined;

  // Deferred action handler - Waits for the submit option state to be updated before handling the action..
  useEffect(() => {
    if (actionTriggered) {
      setActionTriggered(false);
      handleAction(undefined, selectedSubmitOption?.value ?? SubmitOption.Exit);
    }
    // Skip exhaustive deps - This effect is intended to *only* trigger when the selected submit option has updated.
    // (Additional dependencies could cause additional, unintended triggers).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubmitOption]);

  return {
    menuProps,
    label: currentSaveOption.label,
    theme: PrimaryTheme,
    split: options.length > 1,
    options,
    primary: !pristine,
    styles: pristine
      ? {
          root: {
            backgroundColor: grey.base,
          },
          wrapper: {
            order: 4,
          },
        }
      : { wrapper: { order: 4 } },
    disabled: disabled || submitInProgress || fixedDisabled,
    'data-testid': `form-save-${currentSaveOption.label.toLowerCase().replaceAll(' ', '')}`,
    onClick: handleAction,
  };
};

export type ISelectButtonProps = ButtonPropsType & {
  primaryAction?: boolean;
  options: Array<ISubmitOptionEntry>;
  isModal?: boolean;
  disabled?: boolean;
  temporaryDisableDelayOnClick?: number;
};

export const SelectButton = (props: ISelectButtonProps) => {
  const {
    onClick: onClickProp,
    primaryAction,
    options: unfilteredOptions,
    isModal,
    styles: stylesFromProps,
    disabled: disabledProp,
    temporaryDisableDelayOnClick = 2000,
    tooltip,
  } = props;

  const { menuProps, label, theme, options, primary, styles, disabled, onClick } = useSelectButtonProps(
    unfilteredOptions,
    onClickProp!,
    disabledProp,
    temporaryDisableDelayOnClick
  );

  const mergedStyles = merge(styles, stylesFromProps);

  if (isModal || options.length < 2) {
    if (!primary) {
      return (
        <InfoButton {...props} onClick={onClick} disabled={disabled} styles={mergedStyles}>
          {label}
        </InfoButton>
      );
    } else if (primaryAction) {
      return (
        <SuccessButton {...props} onClick={onClick} disabled={disabled} styles={mergedStyles}>
          {label}
        </SuccessButton>
      );
    }
    return (
      <PrimaryButton {...props} onClick={onClick} disabled={disabled} styles={mergedStyles}>
        {label}
      </PrimaryButton>
    );
  }

  return (
    <Button
      data-testid={`form-save-${label.toLowerCase().replaceAll(' ', '')}`}
      text={label}
      primary={primary}
      split
      splitButtonAriaLabel="See save options"
      aria-roledescription="save options"
      menuProps={menuProps}
      onClick={onClick}
      disabled={disabled}
      theme={primaryAction ? SuccessTheme : theme}
      styles={mergedStyles}
      tooltip={tooltip}
    />
  );
};

export const SaveSelectButton = (
  props: Omit<ISelectButtonProps, 'options'> & { saveOptions?: ISelectButtonProps['options'] }
) => <SelectButton {...props} options={props.saveOptions || SAVE_OPTIONS} />;
SaveSelectButton.displayName = 'Button__PrimaryButton';

export const PostSelectButton = (
  props: Omit<ISelectButtonProps, 'options'> & { saveOptions?: ISelectButtonProps['options'] }
) => <SelectButton {...props} options={props.saveOptions || POST_OPTIONS} />;
PostSelectButton.displayName = 'Button__PrimaryButton';
